import { useContext } from 'react';

import { Flex } from '@/new_src/view/components/Flex';
import { Text } from '@/new_src/view/components/Text';

import DeleteButton from 'components/commons/DeleteButton';

import { CheckCircleFill } from '../Icons/CheckCircleFill';
import { EmergencyHomeFill } from '../Icons/EmergencyHomeFill';
import { InfoFill } from '../Icons/InfoFill';
import { WarningFill } from '../Icons/WarningFill';
import ModalContext from './ModalContext';

import { useTheme } from 'styled-components';
import { defaultTheme } from '@/theme';

const mantineColors = defaultTheme.mantineColors;

export type StatusType = 'default' | 'emergency' | 'warning' | 'info' | 'check';

type ModalHeaderProps = {
  title: React.ReactNode;
  status?: StatusType;
  children?: React.ReactNode;
};

export default function ModalHeader({ title, status = 'default', children }: ModalHeaderProps) {
  const { showHeaderBorder, onClose, isFullScreen } = useContext(ModalContext);
  const theme = useTheme();

  if (isFullScreen) {
    return (
      <Flex
        align="center"
        p="8px 8px"
        h="64px"
        bg="white"
        bdb={`1px solid ${defaultTheme.mantineColors.gray[3]}`}
        shrink={0}
      >
        <Flex w="100%" gap={8} align="center">
          <DeleteButton iconSize={24} width={48} height={48} onClick={onClose} />
          {children || (
            <Text style={textStyle} lh="150%" fw="700" size="20px" c="gray.8">
              {title}
            </Text>
          )}
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex
      align="center"
      justify="space-between"
      p="12px 8px 12px 24px"
      shadow={showHeaderBorder ? `inset 0 -1px 0 0 ${theme.mantineColors.gray[3]}` : 'none'}
    >
      <Flex align="center">
        <Flex columnGap={8} align="center">
          <StatusIcon status={status} />
          <Text style={textStyle} lh="150%" fw="700" size="20px" c="gray.8">
            {title}
          </Text>
        </Flex>
        {children}
      </Flex>
      <DeleteButton iconSize={24} width={48} height={48} onClick={onClose} />
    </Flex>
  );
}

type StatusIconProps = {
  status: StatusType;
};

const StatusIcon = ({ status }: StatusIconProps) => {
  if (status === 'emergency') {
    return <EmergencyHomeFill fill={mantineColors.red[6]} />;
  }
  if (status === 'warning') {
    return <WarningFill fill={mantineColors.yellow[6]} />;
  }
  if (status === 'info') {
    return <InfoFill fill={mantineColors.blue[6]} />;
  }
  if (status === 'check') {
    return <CheckCircleFill fill={mantineColors.green[6]} />;
  }

  return null;
};

const textStyle: React.CSSProperties = {
  whiteSpace: 'normal',
  wordBreak: 'break-word',
};
